import { useEffect, useState } from 'react'
import { getInitialSupabaseSession } from './auth'
import { api } from './api'
import type { User } from './types'

const profilePhotoKey = (id: string) => `motion:profile-photo:${id}`
const sharedPhotos = new Map<string, string | null>()

export async function saveProfilePhoto(id: string, photo: Blob | null) {
  const user = photo ? await api.uploadProfilePhoto(photo) : await api.removeProfilePhoto()
  sharedPhotos.set(id, user.avatar_url || null)
  try { window.localStorage.removeItem(profilePhotoKey(id)) } catch { /* A server save does not depend on local storage. */ }
  window.dispatchEvent(new Event('motion-profile-change'))
}

function localProfilePhoto(id: string) {
  try { return window.localStorage.getItem(profilePhotoKey(id)) } catch { return null }
}

function accountPhoto(user: User) {
  return sharedPhotos.has(user.id) ? sharedPhotos.get(user.id) : user.avatar_url || localProfilePhoto(user.id)
}

export function hasCustomProfilePhoto(user: User) {
  return Boolean(accountPhoto(user))
}

export function useAccountProfile(user: User) {
  const defaultPhoto = user.email.toLowerCase() === 'will@willweinbach.com' ? '/avatars/will.png' : null
  const fallback = {
    name: user.email.toLowerCase() === 'will@willweinbach.com' ? 'Will Weinbach' : user.name,
    photo: accountPhoto(user) || defaultPhoto,
  }
  const [profile, setProfile] = useState(fallback)
  useEffect(() => {
    let active = true
    setProfile(fallback)
    void getInitialSupabaseSession().then((session) => {
      if (!active || session?.user.email?.toLowerCase() !== user.email.toLowerCase()) return
      const metadata = session.user.user_metadata
      setProfile({ name: metadata.full_name || metadata.name || fallback.name, photo: accountPhoto(user) || metadata.avatar_url || defaultPhoto })
    }).catch(() => { /* Keep the available account profile when offline. */ })
    return () => { active = false }
  }, [user.id, user.email, user.name, user.avatar_url])
  useEffect(() => {
    const refresh = () => setProfile((current) => ({ ...current, photo: accountPhoto(user) || (user.email.toLowerCase() === 'will@willweinbach.com' ? '/avatars/will.png' : null) }))
    window.addEventListener('motion-profile-change', refresh)
    window.addEventListener('storage', refresh)
    return () => { window.removeEventListener('motion-profile-change', refresh); window.removeEventListener('storage', refresh) }
  }, [user.id, user.email, user.avatar_url])
  return profile
}
